import React from 'react'
import PropTypes from 'prop-types'
import { useMarketplaceSidebarLogic } from './MarketplaceSidebar.logic.js'
import { MarketplaceSidebarView } from './MarketplaceSidebar.view.jsx'
import { MarketplaceFilters } from '../MarketplaceFilters/MarketplaceFilters.jsx'
import { isDrayNowHost } from '../../../../lib/config/multitenancy.js'
import { FEATURES } from '../../../../lib/config/constants.js'
import { canManageOrders } from '../../../../lib/config/roles.js'
import { canAccessOrderOverview } from '../../utils/overviewAccess.js'

/**
 * MarketplaceSidebar
 *
 * Container component for the marketplace sidebar.
 * Integrates with tenant features, user permissions, and marketplace filters.
 */
export const MarketplaceSidebar = ({ tenant, userRole }) => {
  const isDrayNow = isDrayNowHost()

  // Determine visibility flags based on user role and tenant features
  const isAdmin = userRole === 'admin'
  const isImc = userRole === 'imc'
  const showStreetTurns =
    isAdmin && tenant?.isFeatureEnabled?.(FEATURES.STREET_TURN_MANAGEMENT)

  const logic = useMarketplaceSidebarLogic({
    isDrayNow,
    isAdmin,
    showStreetTurns,
  })

  const {
    categories,
    listType,
    marketsCsv,
    search,
    isLoading,
    createOrderUrl,
    streetTurnsUrl,
    streetTurnCount,
    handleCategorySelect,
    filterCount,
  } = logic

  // Determine additional visibility flags
  const showOverview = isAdmin || isImc
  // The Order Overview link itself (MV-3398). The section above also hosts
  // Street Turns / Container Dashboard, so it stays open for admin/imc even
  // when the overview link is gated off.
  const showOrderOverviewLink =
    showOverview &&
    canAccessOrderOverview({
      userRole,
      isDrayNow,
      isFeatureEnabled: tenant?.isFeatureEnabled,
    })
  const showContainerDashboard =
    (isAdmin || isImc) &&
    tenant?.isFeatureEnabled?.(FEATURES.CAN_VIEW_CONTAINER_DASHBOARD)
  const showLanes =
    (isAdmin || isImc) && tenant?.isFeatureEnabled?.(FEATURES.LANES)
  // Order creation is admin/imc only. listType already forces every other role
  // to trips (useMarketplaceParams), but gate explicitly here too so the
  // affordance never depends on a single derived value.
  const showCreateOrder = canManageOrders(userRole)

  // Filter closed category for non-DrayNow hosts
  const visibleCategories = categories.filter((cat) => {
    if (cat.key === 'closed' && !isDrayNow) return false
    return true
  })

  // Determine if filters should be shown
  const isCarrier = userRole === 'carrier'
  const showFilters =
    isAdmin ||
    isImc ||
    (isCarrier &&
      tenant?.isFeatureEnabled?.(FEATURES.AVAILABLE_FILTERING_FIELDS))

  return (
    <MarketplaceSidebarView
      categories={visibleCategories}
      listType={listType}
      marketsCsv={marketsCsv}
      search={search}
      isLoading={isLoading}
      createOrderUrl={createOrderUrl}
      streetTurnsUrl={streetTurnsUrl}
      streetTurnCount={streetTurnCount}
      showOverview={showOverview}
      showOrderOverviewLink={showOrderOverviewLink}
      showStreetTurns={showStreetTurns}
      showContainerDashboard={showContainerDashboard}
      showLanes={showLanes}
      showCreateOrder={showCreateOrder}
      showFilters={showFilters}
      filterCount={filterCount}
      onCategorySelect={handleCategorySelect}
      renderFilters={() => (
        <MarketplaceFilters
          userRole={userRole}
          tenant={tenant}
          listType={listType}
        />
      )}
    />
  )
}

MarketplaceSidebar.propTypes = {
  tenant: PropTypes.shape({
    isFeatureEnabled: PropTypes.func,
    features: PropTypes.object,
  }),
  // Any backend role string. Not an enum: `driver` is a real production role,
  // and callers pass unmodelled fallbacks ('user' from AppLayout, 'guest' from
  // useMarketplaceParams). Capability checks live in lib/config/roles.js.
  userRole: PropTypes.string,
}

MarketplaceSidebar.defaultProps = {
  tenant: null,
  // Fail closed. userRole now drives a capability gate (showCreateOrder), so a
  // permissive default would silently reopen the hole for any caller that
  // forgets the prop. AppLayout always passes it.
  userRole: null,
}
